import { baseApi } from 'src/Services/api.ts'
import { PaginatedResponse } from 'src/Redux/types.ts'

import { Client } from './clientsApi'
import { DocumentStatusEnum, ApprovalStatusEnum } from './documentsApi'

export enum ContractStatusEnum {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  TERMINATED = 'TERMINATED',
}

export interface Contract {
  id: number
  clientId: number
  client: Client
  offerId: number
  status: ContractStatusEnum
  contractNumber: string
  createdAt: string
  updatedAt: string
  googleDocsUrl?: string
  contractDate?: string
  endDate?: string
  workType?: string
  reportingPeriodName?: string
  reportingPeriodDescription?: string
  operationalLogsDocName?: string
  foodRule?: string
  accommodationRule?: string
  rentPeriod?: number
  documentId?: string
  document?: {
    id: string
    status: DocumentStatusEnum
    approvalHistory?: Array<{
      id: string
      status: ApprovalStatusEnum
      order: number
      comment?: string | null
      completedAt?: string | null
      user?: {
        id: number
        email: string
        firstName?: string
        lastName?: string
      }
    }>
  }
}

export interface ContractQueryParams {
  page?: number
  limit?: number
  clientId?: number
  status?: ContractStatusEnum
  search?: string
}

export interface CreateContractBody {
  offerId: number
  contractDate?: string
  endDate?: string
  workType?: string
  reportingPeriodName?: string
  reportingPeriodDescription?: string
  operationalLogsDocName?: string
  foodRule?: string
  accommodationRule?: string
  rentPeriod?: number
}

export interface UpdateContractBody {
  contractDate?: string
  endDate?: string
  workType?: string
  reportingPeriodName?: string
  reportingPeriodDescription?: string
  operationalLogsDocName?: string
  foodRule?: string
  accommodationRule?: string
  rentPeriod?: number
  status?: ContractStatusEnum
}

export enum ContractStatus {
  Draft = 'Черновик',
  OnSign = 'На согласовании',
  Signed = 'Подписан',
}

export const contractsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllContracts: builder.query<
      PaginatedResponse<Contract[]>,
      ContractQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams()

        if (params) {
          if (params.page) queryParams.append('page', params.page.toString())
          if (params.limit) queryParams.append('limit', params.limit.toString())
          if (params.clientId)
            queryParams.append('clientId', params.clientId.toString())
          if (params.status) queryParams.append('status', params.status)
          if (params.search) queryParams.append('search', params.search)
        }

        const queryString = queryParams.toString()
        return `/contracts${queryString ? `?${queryString}` : ''}`
      },

      providesTags: ['contracts'],
    }),

    getContractById: builder.query<PaginatedResponse<Contract>, number>({
      query: (id) => `/contracts/${id}`,
      providesTags: ['contracts'],
    }),

    createContract: builder.mutation<
      PaginatedResponse<Contract>,
      CreateContractBody
    >({
      query: (data) => ({
        url: `/contracts`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['contracts', 'offers'],
    }),

    updateContract: builder.mutation<
      PaginatedResponse<Contract>,
      { id: number; data: UpdateContractBody }
    >({
      query: ({ id, data }) => ({
        url: `/contracts/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['contracts'],
    }),

    deleteContract: builder.mutation<void, number>({
      query: (id) => ({
        url: `/contracts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['contracts'],
    }),

    generateContractDocument: builder.mutation<
      { docId: string; docUrl: string; sharing: string },
      number
    >({
      query: (contractId) => ({
        url: `/contracts/${contractId}/generate-document`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['contracts'],
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetAllContractsQuery,
  useGetContractByIdQuery,
  useCreateContractMutation,
  useUpdateContractMutation,
  useGenerateContractDocumentMutation,
  useDeleteContractMutation,
} = contractsApi
